import * as cx from "classnames/bind";
import { Button } from "components";
import Link from "next/link";
import { useRouter } from "next/router";
import Script from "next/script";
import Head from "next/head";
import { useSession } from "hooks";
import { useTranslation } from "next-i18next";
import { memo, useEffect, useState } from "react";
import { imageUrl, uniqObjArray, translate } from "utils";
import { Range } from "react-range";
import store from "store2";
import s from "styles/Cocktail.module.scss";

const formatSecondsToMinutes = (s) => {
  return (s - (s %= 60)) / 60 + (9 < s ? ":" : ":0") + s;
};

const Slider = ({ onChange, ...props }) => {
  const [values, setValues] = useState(props.sliderValues);
  useEffect(() => {
    setValues(props.sliderValues);
  }, [props.sliderValues]);
  const change = (newValues) => onChange(newValues, props.index);
  return (
    <div className={cx(s.SliderInput)}>
      <Range
        values={values}
        {...props}
        onChange={change}
        renderTrack={({ className, props, children }) => (
          <div
            className={cx(s.SliderInputTrack)}
            onMouseDown={props.onMouseDown}
            onTouchStart={props.onTouchStart}
            style={{ ...props.style }}
          >
            <div className={cx(s.SliderInputFill)} ref={props.ref}>
              {children}
              <div
                className={cx(s.SliderInputBack)}
                style={{ width: `${values}%` }}
              ></div>
            </div>
          </div>
        )}
        renderThumb={({ props, isDragged }) => (
          <div
            className={cx(s.SliderInputThumb)}
            {...props}
            style={{ ...props.style }}
          >
            <div></div>
          </div>
        )}
      />
    </div>
  );
};

const WistiaEmbed = memo(({ videoId, videoIsLoaded }) => {
  return (
    <div
      className={cx("wistia_responsive_padding", s.wistia_responsive_padding)}
    >
      <div
        className={cx("wistia_responsive_wrapper", s.wistia_responsive_wrapper)}
      >
        <div
          className={cx("wistia_video_foam_dummy", s.wistia_video_foam_dummy)}
        ></div>
        <div
          className={cx(
            "wistia_embed",
            `wistia_async_${videoId}`,
            s.wistia_embed,
          )}
          videofoam="true"
        >
          <div className={cx("wistia_swatch", s.wistia_swatch)}>
            <img
              src={`https://fast.wistia.com/embed/medias/${videoId}/swatch`}
              alt="Cocktail video recipe"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

function CocktailVideo({ ...props }) {
  const [cocktail, setCocktail] = useState(props.cocktail);

  useEffect(() => {
    if (props.cocktail?.id != cocktail?.id) {
      setCocktail(props.cocktail);
    }
  }, [props.cocktail]);
  const { t } = useTranslation();
  const videoId = cocktail ? cocktail.recipes[0].timeline.video_embed_id : null;
  const [chapters, setChapters] = useState([]);
  const [preventAutoPlayPause, setPreventAutoPlayPause] = useState(false);
  const [videoIsLoaded, setVideoIsLoaded] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [video, setVideo] = useState(null);
  const [autoPlay, setAutoPlay] = useState(store("autoPlay") || false);
  const [reachedEndOfChapter, setReachedEndOfChapter] = useState(false);
  const watchedVideos = store("watchedVideos") || [];

  const [elapsedTime, setElapsedTime] = useState("");
  const [activeChapter, setActiveChapter] = useState(0);
  const [activeRecipe, setActiveRecipe] = useState(
    cocktail
      ? cocktail.recipes.find((r) => r.default) || cocktail.recipes[0]
      : null,
  );

  const { data: session, status } = useSession();
  const router = useRouter();
  const locale = router.locale;

  const image_url = imageUrl((cocktail || {}).image?.id || "");
  const regex = /(<([^>]+)>)/gi;

  let interval;

  const onSliderChange = (newValues, index) => {
    if (activeChapter == index) {
      chapters.forEach((c) => {
        clearInterval(c.interval);
      });
      const chapter = chapters[activeChapter];
      const time = chapter.length * (newValues[0] / 100) + chapter.time;
      setChapters(
        chapters.map((c, i) => ({
          ...c,
          sliderValues: i == activeChapter ? newValues : [0],
        })),
      );
      video.time(time);
    }
  };

  const changeChapter = (index, progress) => {
    if (index == activeChapter || !video) {
      return;
    }
    video.time(chapters[index].time + 0.01);
    video.play();
    setPreventAutoPlayPause(true);
    setChapters(
      chapters.map((c) => ({
        ...c,
        sliderValues: [0],
      })),
    );
  };
  useEffect(() => {
    if (preventAutoPlayPause) {
      const timer = setTimeout(() => setPreventAutoPlayPause(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [preventAutoPlayPause]);

  useEffect(() => {
    if (video) {
      video.bind("play", () => {
        setPreventAutoPlayPause(true);
      });

      video.bind("pause", () => {
        chapters.forEach((c) => {
          clearInterval(c.interval);
        });
      });

      video.bind("timechange", (currentTime) => {
        const currentChapter = chapters[activeChapter];
        clearInterval(currentChapter.interval);
        if (isMouseDown) {
          return;
        }
        /*
          const currentChapter = chapters.find(
            (c) => c.time <= currentTime - 0.01 && c.end >= currentTime
          );
          */
        let progress = Math.min(
          100,
          ((currentTime - currentChapter.time) / currentChapter.length) * 100,
        );
        currentChapter.sliderValues = [progress];
        setChapters(chapters.map((c) => c));
        if (video.state() == "playing") {
          currentChapter.interval = window.setInterval(() => {
            progress += currentChapter.seekerIntervalLength / 10;
            progress = Math.min(100, progress);
            if (progress >= 100) {
              clearInterval(currentChapter.interval);
            } else {
              currentChapter.sliderValues = [progress];
              setChapters(chapters.map((c) => c));
            }
          }, 20);
        }
      });
      return () => {
        video.unbind("play");
        video.unbind("pause");
        video.unbind("timechange");
      };
    }
  }, [video, activeChapter, isMouseDown, chapters]);
  useEffect(() => {
    if (video) {
      const mousedown = document.addEventListener("mousedown", (e) => {
        setIsMouseDown(true);
      });
      const touchstart = document.addEventListener("touchstart", (e) => {
        setIsMouseDown(true);
      });
      const mouseup = document.addEventListener("mouseup", (e) => {
        setIsMouseDown(false);
      });
      const touchend = document.addEventListener("touchend", (e) => {
        setIsMouseDown(false);
      });
      return () => {
        document.removeEventListener("mousedown", mousedown);
        document.removeEventListener("mouseup", mouseup);
        document.removeEventListener("touchstart", touchstart);
        document.removeEventListener("touchend", touchend);
      };
    }
  }, [video]);
  useEffect(() => {
    if (video) {
      chapters.forEach((chapter, index) => {
        video.bind(
          "betweentimes",
          chapter.time,
          Math.max(chapter.end - 0.01, 0),
          (withinInterval) => {
            if (withinInterval) {
              if (
                !autoPlay &&
                !preventAutoPlayPause &&
                !isMouseDown &&
                index != activeChapter
              ) {
                video.pause();
                //video.time(chapter.time - 0.01);
              } else {
                if (index != activeChapter) {
                  setActiveChapter(index);
                }
              }
            }
          },
        );
      });

      return () => {
        video.unbind("betweentimes");
      };
    }
  }, [video, chapters, activeChapter]);

  useEffect(() => {
    if (video) {
      video.bind("secondchange", (seconds) => {
        setElapsedTime(
          `${formatSecondsToMinutes(seconds)} - ${formatSecondsToMinutes(
            Math.round(video.duration()),
          )}`,
        );
      });
      return () => {
        video.unbind("secondchange");
      };
    }
  }, [video]);
  useEffect(() => {
    window._wq = window._wq || [];
    window._wq.push({
      id: videoId,
      options: {
        muted: false,
        autoPlay,
        settingsControl: false,
        playerColor: "4a90e2",
      },
      onReady: function (video) {
        store("watchedVideos", [...new Set(watchedVideos.concat([videoId]))]);
        let chapters = video.plugin.chapters.options.chapterList.filter(
          (c) => !!!c.deleted,
        );

        chapters.sort((a, b) => a.time - b.time);
        setElapsedTime(
          `${formatSecondsToMinutes(0)} - ${formatSecondsToMinutes(
            Math.round(video.duration()),
          )}`,
        );
        chapters = chapters.map((c, index) => {
          const nextChapter =
            index < chapters.length ? chapters[index + 1] : null;
          const end = nextChapter ? nextChapter.time - 0.01 : video.duration();
          if (c.time === 0) {
            c.time = 0.01;
          }
          const length = end - c.time;

          return {
            ...c,
            seekerIntervalLength: 1000 / length / 50,
            duration: formatSecondsToMinutes(Math.round(length)),
            interval: null,
            index,
            length,
            end,
            sliderValues: [0],
          };
        });
        setChapters(chapters);
        setVideoIsLoaded(true);
        setVideo(video);
        video.play();
      },
    });
  }, []);

  return (
    <>
      <Head>
        <title key="title">Watch {cocktail?.name} recipe - Cocktail Club</title>
        <meta
          key="description"
          name="description"
          content={(cocktail?.description || "").replace(regex, "")}
        />
        <meta key="ogImage" property="og:image" content={image_url} />
        <meta
          property="og:image:alt"
          content={"Watch " + cocktail?.name + " recipe - Cocktail Club"}
        />
        <meta
          property="og:video"
          content={
            "https://cocktailclub.com/cocktails/" + cocktail?.slug + "/play"
          }
        />
        <meta
          property="og:video:type"
          content="application/x-shockwave-flash"
        />
        <meta
          key="ogUrl"
          property="og:url"
          content={
            "https://cocktailclub.com/cocktails/" + cocktail?.slug + "/play"
          }
        />
        <link
          key="canonical"
          rel="canonical"
          href={
            "https://cocktailclub.com/cocktails/" + cocktail?.slug + "/play"
          }
        />

        <meta key="ogType" property="og:type" content="video.movie" />
        <meta
          key="ogTitle"
          property="og:title"
          content={
            "How to make " + cocktail?.name + " - video recipe | Cocktail Club"
          }
        />
        <meta
          key="ogDescription"
          property="og:description"
          content={(cocktail?.description || "").replace(regex, "")}
        />

        <meta property="video:movie" content="Cocktails" />
      </Head>
      <div className={cx("Timeline", s.Timeline)}>
        <div itemScope="" itemID="" itemType="http://schema.org/VideoObject">
          <link
            itemProp="url"
            href={
              "https://cocktailclub.com/cocktails/" + cocktail?.slug + "/play"
            }
          />
          <meta
            itemProp="name"
            content={"How to make " + cocktail?.name + " - video recipe"}
          />
          <meta
            itemProp="description"
            content={(cocktail?.description || "").replace(regex, "")}
          />

          <span
            itemProp="author"
            itemScope=""
            itemType="http://schema.org/Organization"
          >
            <link itemProp="name" content="Cocktail Club" />
          </span>

          <meta itemProp="isFamilyFriendly" content="true" />
          <meta
            itemProp="regionsAllowed"
            content="AD,AE,AF,AG,AI,AL,AM,AO,AQ,AR,AS,AT,AU,AW,AX,AZ,BA,BB,BD,BE,BF,BG,BH,BI,BJ,BL,BM,BN,BO,BQ,BR,BS,BT,BV,BW,BY,BZ,CA,CC,CD,CF,CG,CH,CI,CK,CL,CM,CN,CO,CR,CU,CV,CW,CX,CY,CZ,DE,DJ,DK,DM,DO,DZ,EC,EE,EG,EH,ER,ES,ET,FI,FJ,FK,FM,FO,FR,GA,GB,GD,GE,GF,GG,GH,GI,GL,GM,GN,GP,GQ,GR,GS,GT,GU,GW,GY,HK,HM,HN,HR,HT,HU,ID,IE,IL,IM,IN,IO,IQ,IR,IS,IT,JE,JM,JO,JP,KE,KG,KH,KI,KM,KN,KP,KR,KW,KY,KZ,LA,LB,LC,LI,LK,LR,LS,LT,LU,LV,LY,MA,MC,MD,ME,MF,MG,MH,MK,ML,MM,MN,MO,MP,MQ,MR,MS,MT,MU,MV,MW,MX,MY,MZ,NA,NC,NE,NF,NG,NI,NL,NO,NP,NR,NU,NZ,OM,PA,PE,PF,PG,PH,PK,PL,PM,PN,PR,PS,PT,PW,PY,QA,RE,RO,RS,RU,RW,SA,SB,SC,SD,SE,SG,SH,SI,SJ,SK,SL,SM,SN,SO,SR,SS,ST,SV,SX,SY,SZ,TC,TD,TF,TG,TH,TJ,TK,TL,TM,TN,TO,TR,TT,TV,TW,TZ,UA,UG,UM,US,UY,UZ,VA,VC,VE,VG,VI,VN,VU,WF,WS,YE,YT,ZA,ZM,ZW"
          />
          <meta itemProp="datePublished" content="2022-02-02" />
          <meta itemProp="uploadDate" content="2022-02-02" />
          <meta itemProp="genre" content="Howto &amp; Style" />
        </div>
        <div className={cx("TimelineHeader", s.TimelineHeader)}>
          <button className={cx(s.actionBtn)} onClick={() => router.back()}>
            <img src="/img/icons/chevronLeft.svg" alt="Go back" />
          </button>
          <Link href="/cocktails" className={cx(s.actionBtn)}>
            <img
              src="/img/icons/home-alt-light-white.svg"
              alt="Go to frontpage"
            />
          </Link>
          <div>
            {/*
          <h1>{autoPlay.toString()}</h1>
          <h1>{preventAutoPlayPause.toString()}</h1>
          */}
            <h1>
              <span className="hidden">{t("How to make")} </span>
              {translate(cocktail, "name", locale)}
              <span className="hidden"> - {t("video recipe")}</span>
            </h1>
            {video ? <span>{elapsedTime}</span> : <></>}
          </div>
          <Button
            className={("hollow", { active: autoPlay })}
            onClick={() => {
              setAutoPlay(!autoPlay);
              store("autoPlay", !autoPlay);
            }}
          >
            {autoPlay ? t("Autoplay on") : t("Autoplay off")}
          </Button>
          <Button
            className={cx(s.TimelinePlay, {
              hidden: !video || video.state() != "ended",
            })}
            onClick={() => {
              video.time(0);
              video.play();
            }}
          >
            <img src="/img/icons/replay-circle-light.svg" alt="Replay" />
          </Button>
        </div>
        <div className={cx("TimelineBody", s.TimelineBody)}>
          <div className={cx("TimelineVideoHolder", s.TimelineVideoHolder)}>
            <div className={cx("TimelineVideo", s.TimelineVideo)}>
              <WistiaEmbed videoId={videoId} videoIsLoaded={videoIsLoaded} />
            </div>
            <div className={cx("TimelineControls", s.TimelineControls)}>
              <div
                className={cx("TimelineControlIndex", s.TimelineControlIndex)}
              >
                <span>
                  {`${elapsedTime} ${t("Chapter")} ${activeChapter + 1} / ${
                    chapters.length
                  }`}
                </span>
              </div>
              {video ? (
                <div
                  className={cx(
                    "TimelineControlButtons",
                    s.TimelineControlButtons,
                  )}
                >
                  <button
                    href="#"
                    className={activeChapter == 0 ? s.disabled : ""}
                    onClick={() => {
                      setPreventAutoPlayPause(true);
                      changeChapter(activeChapter - 1);
                    }}
                  >
                    <img
                      src="/img/icons/step-backward-light.svg"
                      alt="Previous chapter"
                    />
                  </button>
                  <button
                    href="#"
                    className={cx(s.TimelinePlay, {
                      hidden: video.state() == "playing",
                    })}
                    onClick={() => {
                      setPreventAutoPlayPause(true);
                      video.play();
                    }}
                  >
                    <img
                      src="/img/icons/play-circle-light.svg"
                      alt="Play video"
                    />
                  </button>
                  <button
                    href="#"
                    className={cx(s.TimelinePause, {
                      hidden: video.state() != "playing",
                    })}
                    onClick={() => {
                      video.pause();
                    }}
                  >
                    <img
                      src="/img/icons/pause-circle-light.svg"
                      alt="Pause video"
                    />
                  </button>
                  <button
                    href="#"
                    className={
                      activeChapter == chapters.length - 1 ? s.disabled : ""
                    }
                    onClick={() => {
                      setPreventAutoPlayPause(true);
                      changeChapter(activeChapter + 1);
                    }}
                  >
                    <img
                      src="/img/icons/step-forward-light.svg"
                      alt="Next chapter"
                    />
                  </button>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
          <div className={cx("TimelineSteps", s.TimelineSteps)}>
            {chapters.map((chapter, index) => {
              const recipeStep = activeRecipe.timeline.steps[index];
              if (!recipeStep) {
                return null;
              }
              return (
                <div
                  className={cx(
                    "TimelineStep",
                    s.TimelineStep,
                    activeChapter == index ? s.Active : "",
                  )}
                  key={index}
                  onClick={() => {
                    changeChapter(index);
                  }}
                >
                  <div className={cx("StepHeader", s.StepHeader)}>
                    <div
                      id="rangeSlider-index"
                      className={cx("StepSlider", s.StepSlider)}
                    >
                      <Slider {...chapter} onChange={onSliderChange} />
                    </div>
                    <div
                      id="StepIndex-index"
                      className={cx("StepIndex", s.StepIndex)}
                    >
                      {index + 1}
                    </div>
                    <div
                      id="StepInfo-index"
                      className={cx("StepInfo", s.StepInfo)}
                    >
                      <span id="StepDuration-index">{chapter.duration}</span>
                      <h2 id="StepIngredient-index">
                        {translate(recipeStep, "title", locale)}
                      </h2>
                    </div>
                  </div>
                  <div className={cx("StepContent", s.StepContent)}>
                    <p>{translate(recipeStep, "description", locale)}</p>
                    {recipeStep.ingredients.length > 0 ? (
                      <ul>
                        <span>{t("Ingredients")}</span>
                        {recipeStep.ingredients.map((ingredient) => {
                          const measurement = ingredient.measurements[0] || {};
                          const metricAmount = measurement.metric_amount
                            ? parseFloat(measurement.metric_amount || 1)
                            : "";
                          return (
                            <li key={ingredient.id}>
                              {metricAmount}{" "}
                              {translate(
                                measurement.metric_unit || {},
                                "name",
                                locale,
                              )}{" "}
                              {translate(ingredient, "name", locale)}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <></>
                    )}
                    {recipeStep.equipments.length > 0 ? (
                      <ul>
                        <span>Equipment</span>
                        {recipeStep.equipments.map((equipment) => {
                          return (
                            <li key={equipment.id}>
                              {translate(equipment, "name", locale)}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Script src={`https://fast.wistia.com/embed/medias/${videoId}.jsonp`} />
      <Script src="https://fast.wistia.com/assets/external/E-v1.js" />
    </>
  );
}

export default CocktailVideo;
